// src/utils/biometricLogin.js
import { Capacitor } from "@capacitor/core";
import {
    BiometricAuth,
    AndroidBiometryStrength,
} from "@aparajita/capacitor-biometric-auth";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";

const BIO_KEY = "stirup_bio_login_v1";

// Segnale "l'utente si è appena disconnesso volontariamente", usato per
// sospendere il SOLO auto-trigger del FaceID/impronta al prossimo arrivo su
// LoginPage (il bottone biometrico manuale resta invece sempre disponibile).
// sessionStorage e non localStorage: vive finché il processo app resta
// vivo, si azzera da solo alla chiusura/kill dell'app, così alla riapertura
// l'auto-login biometrico torna a funzionare come oggi senza bisogno di
// disattivare nulla qui.
const JUST_LOGGED_OUT_KEY = "stirup_just_logged_out";

function isNative() {
    return Capacitor.isNativePlatform();
}

export function isBiometricSupportedRuntime() {
    return isNative();
}

// Da chiamare nei punti di LOGOUT ESPLICITO (bottone premuto dall'utente),
// mai su un logout forzato da sessione scaduta (401/403): in quel caso il
// FaceID deve continuare a poter ri-loggare da solo.
export function markJustLoggedOut() {
    try {
        sessionStorage.setItem(JUST_LOGGED_OUT_KEY, "1");
    } catch {
        // sessionStorage non disponibile: nessun segnale, l'auto-trigger
        // si comporterà come oggi (non è un caso critico).
    }
}

// Legge e CONSUMA il segnale (si azzera dopo la prima lettura): vale solo
// per l'arrivo al login immediatamente successivo al logout, non per visite
// successive alla stessa pagina nella stessa sessione app.
export function consumeJustLoggedOut() {
    try {
        const had = sessionStorage.getItem(JUST_LOGGED_OUT_KEY) === "1";
        if (had) sessionStorage.removeItem(JUST_LOGGED_OUT_KEY);
        return had;
    } catch {
        return false;
    }
}

export async function checkBiometricAvailability() {
    if (!isNative()) {
        return {
            available: false,
            biometryLabel: "",
            reason: "Non disponibile su web",
        };
    }

    try {
        const info = await BiometricAuth.checkBiometry();

        return {
            available: !!info?.isAvailable,
            strongAvailable: !!info?.strongBiometryIsAvailable,
            biometryType: info?.biometryType || "",
            biometryTypes: Array.isArray(info?.biometryTypes)
                ? info.biometryTypes
                : [],
            deviceIsSecure: !!info?.deviceIsSecure,
            reason: info?.reason || "",
            biometryLabel: getBiometryLabel(info?.biometryType),
        };
    } catch (err) {
        return {
            available: false,
            biometryLabel: "",
            reason: err?.message || "Biometria non disponibile",
        };
    }
}

function getBiometryLabel(type) {
    const v = String(type || "").toLowerCase();
    if (v.includes("face")) return "Face ID";
    if (v.includes("touch")) return "Touch ID";
    if (v.includes("finger")) return "Impronta";
    if (v.includes("iris")) return "Iride";
    return "Biometria";
}

export async function saveBiometricLoginCredentials({
    identifier,
    password,
}) {
    if (!isNative()) return false;
    if (!identifier || !password) return false;

    const payload = JSON.stringify({
        identifier: String(identifier).trim(),
        password: String(password),
        savedAt: new Date().toISOString(),
    });

    await SecureStoragePlugin.set({
        key: BIO_KEY,
        value: payload,
    });

    return true;
}

export async function getBiometricLoginCredentials() {
    if (!isNative()) return null;

    try {
        const res = await SecureStoragePlugin.get({ key: BIO_KEY });
        const raw = res?.value || "";
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed?.identifier || !parsed?.password) return null;

        return {
            identifier: String(parsed.identifier),
            password: String(parsed.password),
            savedAt: parsed.savedAt || null,
        };
    } catch {
        return null;
    }
}

export async function hasBiometricLoginCredentials() {
    const creds = await getBiometricLoginCredentials();
    return !!(creds?.identifier && creds?.password);
}

export async function clearBiometricLoginCredentials() {
    if (!isNative()) return false;

    try {
        await SecureStoragePlugin.remove({ key: BIO_KEY });
        return true;
    } catch {
        return false;
    }
}

export async function promptBiometricUnlock() {
    if (!isNative()) {
        throw new Error("Biometria disponibile solo su app mobile");
    }

    await BiometricAuth.authenticate({
        reason: "Accedi rapidamente al tuo account Stirup",
        cancelTitle: "Annulla",
        allowDeviceCredential: true,
        iosFallbackTitle: "Usa codice",
        androidTitle: "Accesso rapido",
        androidSubtitle: "Autenticati per accedere a Stirup",
        androidConfirmationRequired: false,
        androidBiometryStrength: AndroidBiometryStrength.weak,
    });
}

export function getBiometricDisplayLabelFromAvailability(info) {
    return info?.biometryLabel || "Biometria";
}

export function isBiometricNativeRuntime() {
    return isNative();
}